import { useMemo } from 'react';
import useRetrieveAutomationRunsWithDatasets from '@/hooks/api/runs/useRetrieveAutomationRunsWithDatasets.ts';
import { ManualTriggerInput } from '@/types/automationSteps.ts';
import { RunWithDatasets } from '@/types/runs.ts';
import { flattenInfiniteQueryData } from '@/utils/misc.ts';
import {
  buildRunMatch,
  groupMatches,
  isPerfectRunMatch,
  type RunGroup,
} from '@/pages/AutomationPage/components/ManualRunModal/hooks/runGroupMatching.ts';

const useRunGroupsByTriggerDatasets = (triggers: ManualTriggerInput[], automationId: string) => {
  const { data: runs, isPending: isLoadingRuns } = useRetrieveAutomationRunsWithDatasets({
    search: '',
    providedAutomationId: automationId,
  });

  const { perfectRunGroups, partialRunGroups } = useMemo(() => {
    const flattenedRuns = flattenInfiniteQueryData(runs) as RunWithDatasets[];
    const perfectMatches: RunGroup[] = [];
    const partialMatches: RunGroup[] = [];

    for (const run of flattenedRuns) {
      const match = buildRunMatch(triggers, run);
      if (!match) continue;

      if (isPerfectRunMatch(match)) {
        perfectMatches.push(match);
      } else {
        partialMatches.push(match);
      }
    }

    return {
      perfectRunGroups: groupMatches(perfectMatches),
      partialRunGroups: groupMatches(partialMatches, true),
    };
  }, [runs, triggers]);

  return {
    perfectRunGroups,
    partialRunGroups,
    isLoadingRuns,
  };
};

export default useRunGroupsByTriggerDatasets;
